/**
 * Phase 48 — `studio_secrets` kind taxonomy.
 *
 * Replaces the AES-256-GCM `secrets.ts` module deleted in the same
 * phase commit. Lists the known vendor-credential kinds Layer's
 * connectors read from `studio_secrets.value` (plaintext, RLS-isolated,
 * Supabase disk-encrypted; `SERVER_REDACTED_COLUMNS["studio_secrets"]
 * = ['value']` keeps the column off the `/data/studio_secrets` browser).
 *
 * Locked sub-decision H (phase 48 brief). Single source of truth for:
 *
 *   - the chokepoint's `edit_studio_secrets` + `reveal_studio_secret`
 *     + `list_studio_secrets_presence` actions (unknown kind → 400
 *     `secret_kind_unknown` at the implementation layer)
 *   - the dashboard's `/config/:slug` section #9 "Vendor credentials"
 *     editor (renders one form row per kind in this exact order)
 *   - the CLI `secrets set --kind` allowlist (mirrors the same set;
 *     previously a hand-maintained list in `secrets-set.ts`)
 *
 * Order is human-grouped by vendor (stripe first, klaviyo second,
 * resend third, whatsapp fourth) and stable across
 * phases — operators bookmark Reveal-button positions; reordering
 * shifts the UI.
 *
 * Phase 48 shipped eleven kinds; the array has grown since — `KNOWN_SECRET_KINDS.length` is the live count. Adding a kind: append
 * to this array, add its label to `KNOWN_SECRET_KIND_LABELS` and its
 * operator note to `KNOWN_SECRET_KIND_META` (both are `Record<KnownSecretKind, …>`,
 * so the compiler refuses an omission); the dashboard rows, the three
 * secret actions and the CLI allow-list all derive from here. Since
 * vendor-credentials-truth (2026-10-22) `tests/secret-kinds.test.ts` also
 * fails when a resolver reads a `studio_secrets` kind this array lacks —
 * `transcription.api_key` was read for four months without being listed,
 * so the page could neither show nor set it.
 */

/**
 * Every recognised `studio_secrets.kind` value. Strict `readonly`
 * tuple so TypeScript narrows accepted strings at the call site.
 */
export const KNOWN_SECRET_KINDS = [
  // ----- Stripe (3) -----
  "stripe.webhook_signing_secret",
  // 2026-05-31 — second Stripe webhook signing secret for a SECOND
  // Stripe account feeding the same `/webhooks/stripe/<slug>` endpoint.
  // E-Pulsive's real CodexFit subscriptions live on a different Stripe
  // account (`acct_18txST…`) than the test/CRM-V1 account (`acct_1SRJsU…`).
  // Both POST to the one receiver endpoint; the connector verifies the
  // incoming signature against EITHER secret (try-both). The test secret
  // stays under `stripe.webhook_signing_secret`; this kind holds the live
  // CodexFit account's `whsec_…`.
  "stripe.webhook_signing_secret_live",
  "stripe.api_key",
  // 2026-07-27 — the LIVE account's outbound API key, added when Stripe was
  // split into two independently-switchable connectors (stripe_live /
  // stripe_test). `stripe.api_key` above stays the TEST/sandbox key so the
  // existing rows keep working; which one an outbound call uses is chosen
  // EXPLICITLY by `stripe.outbound_environment` in the L2 config, never
  // inferred from which key happens to be present.
  "stripe.api_key_live",
  // ----- Klaviyo (1) -----
  "klaviyo.webhook_bearer_token",
  // ----- Resend (2) -----
  "resend.api_key",
  "resend.webhook_signing_secret",
  // ----- WhatsApp (4) -----
  "whatsapp.access_token",
  "whatsapp.app_secret",
  "whatsapp.phone_number_id",
  "whatsapp.verify_token",
  // ----- Telegram (1) — 2026-07-06 -----
  // Bot token for Layer's internal-ops Telegram channel (PA + staff
  // alerts → management). Env fallback: LAYER_TELEGRAM_BOT_TOKEN.
  "telegram.bot_token",
  // ----- Instagram (5) — 2026-07-06 / DM reply 2026-07-08 -----
  // Long-lived Meta Graph token + the connected IG Business account id
  // for `post_instagram`. Env fallbacks: LAYER_INSTAGRAM_ACCESS_TOKEN /
  // LAYER_INSTAGRAM_BUSINESS_ACCOUNT_ID.
  "instagram.access_token",
  "instagram.business_account_id",
  // 2026-07-08 — Instagram DM reply subsystem. A dedicated Page token for
  // the Messaging API (`instagram_manage_messages`) — OPTIONAL: the DM
  // resolver falls back to `instagram.access_token` when this is absent
  // (the shared @epulsive Page token already carries manage_messages).
  "instagram.dm_access_token",
  // The Instagram-Login sub-app's own secret: the HMAC key of every IG
  // `messages` and `comments` webhook delivery. REQUIRED for a studio on the
  // sub-app. The resolver still falls back to `whatsapp.app_secret` when this
  // row is absent — a fallback from the days both channels shared one Meta
  // app — but since the Instagram-Login sub-app split they are DIFFERENT apps
  // with different secrets (confirmed by hash 2026-10-22), so that fallback
  // fails every delivery; reset each secret in its own app.
  "instagram.app_secret",
  // GET-handshake verify token for the IG `messages` webhook subscription
  // (a value we mint and enter in the Meta app console). Env fallback:
  // LAYER_INSTAGRAM_VERIFY_TOKEN.
  "instagram.verify_token",
  // ----- Gmail (1) — 2026-07-08 -----
  // Google Workspace service-account key JSON (domain-wide delegation) for
  // the Gmail connector — sends + scans mail AS `gmail.impersonate_user`
  // (a real Workspace mailbox, e.g. contact@e-pulsive.co.uk). Per-studio
  // credential; NO env fallback (unlike telegram/instagram).
  "gmail.service_account_key",
  // ----- Website analytics (3) — 2026-07-24 -----
  // Read-side credentials for the `website_analytics_daily` cron saga
  // (dashboard `/analytics` page). All three are per-studio, NO env
  // fallback. Non-secret ids (pixel id, Clarity project id, GA4 property
  // id, hostnames) live in the L2 `website_analytics` config block.
  //
  // Long-lived Meta Marketing/Graph user token used READ-ONLY for pixel
  // stats. 60-day expiry — refreshed together with the other holders of
  // the same token (AgentFlow/.env + the website's CAPI bundle).
  "meta.ads_access_token",
  // Microsoft Clarity Data Export API token (project settings → Data
  // Export). Budget: 10 requests/project/day — the daily saga spends ≤5.
  "clarity.api_token",
  // Google Cloud service-account key JSON with the Analytics Data API
  // enabled + the SA granted Viewer on the GA4 property. Same RS256
  // JWT technique as `gmail.service_account_key`, different scope
  // (analytics.readonly) and usually a different service account.
  "ga4.service_account_key",
  // ----- WhatsApp coexistence (1) — 2026-08-25 -----
  // The id of the WABA the studio SENDS from. Embedded Signup onboarding
  // mints a WABA id (the coexistence flow's FINISH event carries it); the
  // popup completion parks it as `whatsapp.pending_waba_id` below and the
  // Activate step on /whatsapp-signup promotes it here, together with the
  // token and phone that belong to it (wa-subscribe-at-activate,
  // 2026-10-27 — before that the completion wrote this live row at once,
  // pairing the new WABA with the old token until Activate). Non-secret,
  // co-located with the whatsapp.* credentials for unified per-studio
  // management — same posture as `whatsapp.phone_number_id` above. Read
  // since newsletter-meta-limit-guard (2026-10-02) before every newsletter
  // Check and send to read Meta's messaging limit — without it every
  // newsletter is refused (`whatsapp_newsletter_meta_unreadable`,
  // `waba_id_missing`).
  "whatsapp.waba_id",
  // ----- WhatsApp coexistence (3) — 2026-08-25 -----
  // The Embedded Signup completion PARKS the newly-minted business token
  // + resolved phone_number_id under these pending kinds instead of the
  // live `whatsapp.access_token` / `whatsapp.phone_number_id` rows: the
  // send-path resolvers SELECT the live kinds per call, so writing them
  // directly would repoint production onto a WABA whose message
  // templates are not yet approved (runbook §7 ordering). The explicit
  // Activate step on /whatsapp-signup subscribes the Meta app to the
  // parked WABA, then promotes pending → live and deletes these rows.
  // `whatsapp.pending_waba_id` (2026-10-27) is the parked WABA: the app is
  // NOT subscribed to it until Activate, so the new number's inbound
  // cannot reach the AI while every reply would still leave from the old
  // number (the 131047 failure of the 2026-10-27 clavis onboarding). Every
  // `whatsapp.pending_<x>` kind has a live `whatsapp.<x>` twin it is
  // promoted to (pinned by tests/secret-kinds.test.ts).
  "whatsapp.pending_access_token",
  "whatsapp.pending_phone_number_id",
  "whatsapp.pending_waba_id",
  // ----- Google Ads (1) — 2026-10-06 -----
  // Google Cloud service-account key JSON for the Google Ads connector: the
  // account is a user of the studio's Google Ads account, and the key mints
  // short-lived tokens for the Data Manager API (conversion uploads, scope
  // datamanager) and the Google Ads API (scope adwords). No developer token
  // exists any more. Per-studio, NO env fallback. The non-secret ids
  // (customer id, conversion action) live in the L2 `google_ads` block.
  "google_ads.service_account_key",
  // ----- Transcription (1) — read since phase 59, listed 2026-10-22 -----
  // Groq Whisper api key for WhatsApp voice notes. The receiver's
  // `PostgresTranscriptionApiKeyResolver` (`packages/connectors/transcription/
  // src/resolver.ts`) has SELECTed this kind since 2026-06 with
  // `LAYER_TRANSCRIPTION_API_KEY` as the env fallback, but the kind was never
  // appended here — so the dashboard could not show, set or rotate it and
  // the CLI refused it as `secret_kind_unknown`. Env stays the primary
  // source (one Groq account at MVP); a row is the per-studio override.
  "transcription.api_key",
] as const;

/** Type-narrowed union of the known kinds (compile-time check). */
export type KnownSecretKind = (typeof KNOWN_SECRET_KINDS)[number];

/**
 * Read-only `Set` view for cheap membership tests. The `Set<string>`
 * type is intentional so callers can pass arbitrary strings (the
 * implementation raises `secret_kind_unknown` on misses).
 */
export const KNOWN_SECRET_KINDS_SET: ReadonlySet<string> = new Set(
  KNOWN_SECRET_KINDS,
);

/** True iff `kind` is one of the recognised secret-kind labels. */
export function isKnownSecretKind(kind: string): kind is KnownSecretKind {
  return KNOWN_SECRET_KINDS_SET.has(kind);
}

/**
 * Human-readable label per kind. The dashboard's section #9 uses these
 * as the form-row label so operators don't have to mentally translate
 * the snake_case key. Defaults preserve the kind itself for any future
 * kind that lacks an entry (forward-compat for an added kind that
 * forgot a label).
 */
export const KNOWN_SECRET_KIND_LABELS: Readonly<
  Record<KnownSecretKind, string>
> = {
  "stripe.webhook_signing_secret": "Stripe · Webhook signing secret",
  "stripe.webhook_signing_secret_live":
    "Stripe · Webhook signing secret (live CodexFit account)",
  "stripe.api_key": "Stripe · API key (test / sandbox account)",
  "stripe.api_key_live": "Stripe · API key (live CodexFit account)",
  "klaviyo.webhook_bearer_token": "Klaviyo · Webhook bearer token",
  "resend.api_key": "Resend · API key",
  "resend.webhook_signing_secret": "Resend · Webhook signing secret",
  "whatsapp.access_token": "WhatsApp · Access token",
  "whatsapp.app_secret": "WhatsApp · App secret",
  "whatsapp.phone_number_id": "WhatsApp · Phone number ID",
  "whatsapp.verify_token": "WhatsApp · Verify token",
  "telegram.bot_token": "Telegram · Bot token (PA + staff alerts)",
  "instagram.access_token": "Instagram · Access token (Graph API)",
  "instagram.business_account_id": "Instagram · Business account id",
  "instagram.dm_access_token":
    "Instagram · DM access token (Messaging API — optional, falls back to Access token)",
  "instagram.app_secret":
    "Instagram · App secret (webhook HMAC — the Instagram-Login sub-app's own secret)",
  "instagram.verify_token": "Instagram · Webhook verify token",
  "gmail.service_account_key":
    "Gmail · Service-account key (Workspace domain-wide delegation)",
  "meta.ads_access_token":
    "Meta · Ads/Graph access token (pixel stats, read-only — 60-day)",
  "clarity.api_token": "Clarity · Data Export API token",
  "ga4.service_account_key":
    "GA4 · Service-account key (Analytics Data API, read-only)",
  "whatsapp.waba_id": "WhatsApp · WABA id (Embedded Signup)",
  "whatsapp.pending_access_token":
    "WhatsApp · PENDING access token (Embedded Signup, pre-activation)",
  "whatsapp.pending_phone_number_id":
    "WhatsApp · PENDING phone number id (Embedded Signup, pre-activation)",
  "whatsapp.pending_waba_id":
    "WhatsApp · PENDING WABA id (Embedded Signup, pre-activation)",
  "google_ads.service_account_key":
    "Google Ads · Service-account key (conversion uploads)",
  "transcription.api_key":
    "Transcription · Groq Whisper API key (WhatsApp voice notes — optional, overrides the env key)",
};

/**
 * What an operator must know before touching a row: what reads it, when a
 * saved value takes effect, and which Railway variable (if any) stands in
 * when the row is absent. Rendered under every row of the dashboard's
 * Vendor credentials section (vendor-credentials-truth, 2026-10-22) in
 * place of a local hint map that covered one kind; gated by
 * `tests/secret-kinds.test.ts` (non-empty, and every `envFallback` names a
 * `PLATFORM_SECRETS` entry that points back at the kind).
 */
export interface SecretKindMeta {
  /** One to three operator-facing sentences. No value ever appears here. */
  readonly note: string;
  /**
   * `identifier` marks a value that is not a secret (a phone-number id, a
   * WABA id) kept in `studio_secrets` only so a vendor's credentials sit
   * together. The page tags such rows so nobody "rotates" an id.
   */
  readonly sensitivity: "secret" | "identifier";
  /**
   * The Railway variable the resolver reads when no row exists. A row
   * ALWAYS wins over the variable for its studio. Omitted = the row is the
   * only source and an absent row fails the call.
   */
  readonly envFallback?: string;
  /** `warning` renders the note loud: setting this row is usually a mistake. */
  readonly emphasis?: "warning";
}

export const KNOWN_SECRET_KIND_META: Readonly<
  Record<KnownSecretKind, SecretKindMeta>
> = {
  "stripe.webhook_signing_secret": {
    note:
      "Verifies the signature on every Stripe webhook from the TEST account; the receiver tries this and the live secret in turn. Read per delivery, so a saved value is live at once. Stripe keeps a rolled secret valid for up to 24 hours.",
    sensitivity: "secret",
  },
  "stripe.webhook_signing_secret_live": {
    note:
      "The same check for the LIVE account's webhooks. Read per delivery; live at once.",
    sensitivity: "secret",
  },
  "stripe.api_key": {
    note:
      "Outbound Stripe calls while the L2 config says stripe.outbound_environment: test. Read per call; live at once.",
    sensitivity: "secret",
  },
  "stripe.api_key_live": {
    note:
      "Outbound Stripe calls while stripe.outbound_environment is live. Which key a call uses is chosen by that config field, never by which key happens to be present. Read per call; live at once.",
    sensitivity: "secret",
  },
  "klaviyo.webhook_bearer_token": {
    note:
      "Bearer token every Klaviyo webhook must present. Read per delivery; live at once.",
    sensitivity: "secret",
  },
  "resend.api_key": {
    note:
      "Sends every email through Resend. Read per send; live at once. Supabase Auth's own SMTP password (smtp.resend.com) is a separate copy, changed in the Supabase dashboard, not here.",
    sensitivity: "secret",
  },
  "resend.webhook_signing_secret": {
    note:
      "Verifies Resend's delivery webhooks. Read per delivery; live at once.",
    sensitivity: "secret",
  },
  "whatsapp.access_token": {
    note:
      "Meta system-user token behind every WhatsApp send and media download. Read per call; live at once.",
    sensitivity: "secret",
  },
  "whatsapp.app_secret": {
    note:
      "App secret of the WhatsApp Meta app: verifies webhook HMACs and completes Embedded Signup's code exchange; the Instagram webhook falls back to it. Read per delivery; live at once.",
    sensitivity: "secret",
  },
  "whatsapp.phone_number_id": {
    note:
      "Id of the sending phone number. Not a secret — but changing it repoints every send.",
    sensitivity: "identifier",
  },
  "whatsapp.verify_token": {
    note:
      "Token Meta echoes on the webhook GET handshake; must match what is entered in the Meta app console. Read per handshake.",
    sensitivity: "secret",
  },
  "telegram.bot_token": {
    note:
      "Normally NOT set, and 'not set' is the healthy state: every process reads the staff-alert bot's token from its own LAYER_TELEGRAM_BOT_TOKEN. A row here wins over that variable for this studio on every send AND on the webhook secret check (Telegram presents sha256 of the token), so a wrong value silently moves staff alerts and approval buttons onto the wrong bot — Peter's GUARDS G9: never create this row. Rotate the token on Railway instead, then re-run setWebhook.",
    sensitivity: "secret",
    envFallback: "LAYER_TELEGRAM_BOT_TOKEN",
    emphasis: "warning",
  },
  "instagram.access_token": {
    note:
      "Graph API token for posting, comment replies and insights. Read per call; live at once.",
    sensitivity: "secret",
    envFallback: "LAYER_INSTAGRAM_ACCESS_TOKEN",
  },
  "instagram.business_account_id": {
    note: "The connected Instagram Business account id. Not a secret.",
    sensitivity: "identifier",
    envFallback: "LAYER_INSTAGRAM_BUSINESS_ACCOUNT_ID",
  },
  "instagram.dm_access_token": {
    note:
      "Messaging API token for DMs. Optional: when absent the DM sender uses the variable, then the Access token row. Read per call; live at once.",
    sensitivity: "secret",
    envFallback: "LAYER_INSTAGRAM_DM_ACCESS_TOKEN",
  },
  "instagram.app_secret": {
    note:
      "App secret of the studio's own Instagram-Login sub-app, not the parent Meta app's: it verifies the HMAC on every Instagram webhook delivery. Reset it on the sub-app's 'API setup with Instagram login' page, never under Settings > Basic (that is the parent Meta app's secret); steps in build-output/runbooks/railway-deploy.md → 'Env-var rotation'. Read per delivery with no cache, so a Save is live at once; Meta documents no overlap, so assume the old value stops at Reset and store the new one within minutes (Meta retries refused deliveries). Never delete this row: without it every delivery fails (a 401 against the WhatsApp app secret row, a 500 when there is neither).",
    sensitivity: "secret",
    envFallback: "LAYER_INSTAGRAM_APP_SECRET",
  },
  "instagram.verify_token": {
    note:
      "Token Meta echoes on the Instagram webhook GET handshake ('Verify and save' in the Meta console). It gates only that handshake: deliveries are POSTs, HMAC-checked against the App secret row. Read per handshake, so a Save is live at once. If it is ever changed, set it here first, then in every Meta webhook config that uses it.",
    sensitivity: "secret",
    envFallback: "LAYER_INSTAGRAM_VERIFY_TOKEN",
  },
  "gmail.service_account_key": {
    note:
      "Google Workspace service-account JSON (domain-wide delegation) for reading and sending as the studio's mailboxes. Sends pick up a new key on the next call, but the webhook-receiver's inbox scanner caches its Gmail client for the life of the process — redeploy the receiver before deleting the old key in Google Cloud.",
    sensitivity: "secret",
  },
  "meta.ads_access_token": {
    note:
      "60-day Meta Marketing API user token, read-only, for the daily website-analytics snapshot (pixel stats). Live at once. The website's CAPI build holds a separate copy.",
    sensitivity: "secret",
  },
  "clarity.api_token": {
    note:
      "Microsoft Clarity Data Export token for the daily analytics snapshot (budget: 10 requests a day). Live at once.",
    sensitivity: "secret",
  },
  "ga4.service_account_key": {
    note:
      "Google Cloud service-account JSON with the Analytics Data API, Viewer on the GA4 property, for the daily analytics snapshot. Live at once.",
    sensitivity: "secret",
  },
  "whatsapp.waba_id": {
    note:
      "WhatsApp Business Account id the studio sends from, written by Activate on /whatsapp-signup. Read before every newsletter check and send to fetch Meta's messaging limit; without it newsletters are refused.",
    sensitivity: "identifier",
  },
  "whatsapp.pending_access_token": {
    note:
      "Parked by Embedded Signup until Activate on /whatsapp-signup promotes it to the live row. No send path reads it.",
    sensitivity: "secret",
  },
  "whatsapp.pending_phone_number_id": {
    note:
      "Parked by Embedded Signup beside the pending token; promoted by the same Activate step. Not a secret.",
    sensitivity: "identifier",
  },
  "whatsapp.pending_waba_id": {
    note:
      "Parked by Embedded Signup beside the pending token. Activate subscribes the app to this WABA, so the new number's messages reach the AI only from then on, and promotes it to the live WABA id. Resubmit templates on this WABA before Activate.",
    sensitivity: "identifier",
  },
  "google_ads.service_account_key": {
    note:
      "Google Cloud service-account JSON for Google Ads conversion uploads; the hourly upload cron lists only studios that hold it. Live at once.",
    sensitivity: "secret",
  },
  "transcription.api_key": {
    note:
      "Groq Whisper key that turns WhatsApp voice notes into text. The variable on the webhook-receiver is the usual source; a row here overrides it for this studio from the next voice note.",
    sensitivity: "secret",
    envFallback: "LAYER_TRANSCRIPTION_API_KEY",
  },
};

/**
 * Stable wire-error class for the unknown-kind rejection path. Surfaces
 * on the chokepoint's `mapDispatchErrorToStatus` table as a NEW explicit
 * case `secret_kind_unknown → 400` (the bare `_unknown` suffix has no
 * suffix rule in the dispatch table).
 */
export class SecretKindUnknownError extends Error {
  readonly code = "secret_kind_unknown";
  constructor(kind: string) {
    super(
      `secret_kind_unknown: '${kind}' is not in KNOWN_SECRET_KINDS (${KNOWN_SECRET_KINDS.length} kinds: ${KNOWN_SECRET_KINDS.join(", ")})`,
    );
    this.name = "SecretKindUnknownError";
  }
}
